import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  Stack,
  Card,
  CardContent,
  useTheme,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import { useLanguage } from '../../contexts/LanguageContext';
import AccountSelector from '../common/AccountSelector';
import CostCenterSelector from '../common/CostCenterSelector';
import FiscalYearSelector from '../common/FiscalYearSelector';
import { journalEntriesService, JournalEntryDto, CreateJournalEntryDto } from '../../services/journalEntries.service';
import { inventoryOpeningBalancesService } from '../../services/inventoryOpeningBalances.service';
import { InventoryOpeningBalanceSummary } from '../../types/inventoryOpeningBalances.types';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

interface OpeningEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  entry?: JournalEntryDto;
}

interface DetailLine {
  id: string;
  lineNumber: number;
  accountId: number | null;
  accountCode?: string;
  accountName?: string;
  accountNameAr?: string;
  debit: number;
  credit: number;
  costCenterId: number | null;
  description: string;
  isInventory: boolean;
  sourceDocumentNumber?: string;
}

const translations = {
  en: {
    title: 'Opening Entry',
    newTitle: 'New Opening Entry',
    editTitle: 'Edit Opening Entry',
    voucherInfo: 'Entry Information',
    entryNumber: 'Entry Number',
    entryDate: 'Date',
    fiscalPeriod: 'Fiscal Period',
    description: 'Description',
    descriptionAr: 'Description (Arabic)',
    notes: 'Notes',
    inventorySection: '📦 Inventory Accounts (Auto-loaded)',
    inventoryNote: 'These accounts are automatically loaded from posted Inventory Opening Balances',
    manualSection: '📝 Other Accounts (Manual Entry)',
    account: 'Account',
    debit: 'Debit',
    credit: 'Credit',
    costCenter: 'Cost Center',
    addLine: 'Add Line',
    total: 'Total',
    totalDebit: 'Total Debit',
    totalCredit: 'Total Credit',
    difference: 'Difference',
    save: 'Save',
    cancel: 'Cancel',
    balanced: 'Balanced',
    unbalanced: 'Unbalanced - Cannot save',
    noInventory: 'No posted inventory opening balances found',
    loadingInventory: 'Loading inventory balances...',
    sourceDoc: 'Source',
    processing: 'Processing...',
    success: 'Opening entry saved successfully',
    error: 'An error occurred',
  },
  ar: {
    title: 'القيد الافتتاحي',
    newTitle: 'قيد افتتاحي جديد',
    editTitle: 'تعديل القيد الافتتاحي',
    voucherInfo: 'معلومات القيد',
    entryNumber: 'رقم القيد',
    entryDate: 'التاريخ',
    fiscalPeriod: 'الفترة المحاسبية',
    description: 'الوصف',
    descriptionAr: 'الوصف بالعربي',
    notes: 'ملاحظات',
    inventorySection: '📦 حسابات المخزون (تحميل تلقائي)',
    inventoryNote: 'يتم تحميل هذه الحسابات تلقائياً من سندات المخزون الافتتاحية المرحّلة',
    manualSection: '📝 الحسابات الأخرى (إدخال يدوي)',
    account: 'الحساب',
    debit: 'مدين',
    credit: 'دائن',
    costCenter: 'مركز التكلفة',
    addLine: 'إضافة سطر',
    total: 'الإجمالي',
    totalDebit: 'إجمالي المدين',
    totalCredit: 'إجمالي الدائن',
    difference: 'الفرق',
    save: 'حفظ',
    cancel: 'إلغاء',
    balanced: 'متوازن',
    unbalanced: 'غير متوازن - لا يمكن الحفظ',
    noInventory: 'لا توجد سندات مخزون افتتاحية مرحّلة',
    loadingInventory: 'جاري تحميل أرصدة المخزون...',
    sourceDoc: 'المصدر',
    processing: 'جاري المعالجة...',
    success: 'تم حفظ القيد الافتتاحي بنجاح',
    error: 'حدث خطأ',
  },
};

const OpeningEntryDialog: React.FC<OpeningEntryDialogProps> = ({
  open,
  onClose,
  onSuccess,
  entry
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const t = translations[language];
  const isEdit = !!entry;

  const { minDate, maxDate } = useFiscalDateRange();

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [entryNumber, setEntryNumber] = useState<string>('');
  const [entryDate, setEntryDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [fiscalPeriodId, setFiscalPeriodId] = useState<number | null>(null);
  const [description, setDescription] = useState<string>('');
  const [descriptionAr, setDescriptionAr] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [inventoryLines, setInventoryLines] = useState<DetailLine[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [manualLines, setManualLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open) {
      if (entry) {
        loadEntry();
      } else {
        resetForm();
        loadCurrentFiscalPeriod();
        fetchNextEntryNumber();
      }
      // تحميل أرصدة المخزون دائماً عند فتح المودل
      loadInventoryBalances(null);
    }
  }, [open]);

  // إعادة تحميل عند تغيير entry
  useEffect(() => {
    if (open && entry) {
      loadEntry();
    }
  }, [entry]);

  const handleRefreshInventory = async () => {
    setLoadingInventory(true);
    try {
      // استخدام undefined للحصول على جميع السندات المرحّلة
      const summaries = await inventoryOpeningBalancesService.getSummaryForOpeningEntry();
      
      if (summaries && summaries.length > 0) {
        const lines: DetailLine[] = summaries.map((summary, index) => ({
          id: `inv-${summary.openingBalanceId}-${Date.now()}`,
          lineNumber: index + 1,
          accountId: summary.inventoryAccountId || null,
          accountCode: summary.accountCode,
          accountName: summary.accountName,
          accountNameAr: summary.accountNameAr,
          debit: summary.totalValue,
          credit: 0,
          costCenterId: null,
          description: isRTL 
            ? `مخزون ${summary.warehouseNameAr || summary.branchNameAr || ''}`
            : `Inventory ${summary.warehouseName || summary.branchName || ''}`,
          isInventory: true,
          sourceDocumentNumber: summary.documentNumber,
        }));
        setInventoryLines(lines);
      } else {
        setInventoryLines([]);
      }
    } catch (err) {
      console.error('Failed to load inventory balances:', err);
      setInventoryLines([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  const resetForm = () => {
    setEntryNumber('');
    setEntryDate(new Date().toISOString().split('T')[0]);
    setDescription(isRTL ? 'قيد افتتاحي' : 'Opening Entry');
    setDescriptionAr('قيد افتتاحي');
    setNotes('');
    setInventoryLines([]);
    setManualLines([createNewLine(1)]);
    setError('');
  };

  const loadCurrentFiscalPeriod = async () => {
    try {
      const response = await apiClient.get('/FiscalPeriods/current');
      if (response.data?.fiscalPeriodId) {
        setFiscalPeriodId(response.data.fiscalPeriodId);
      }
    } catch (err) {
    }
  };

  const fetchNextEntryNumber = async () => {
    try {
      const nextNumber = await journalEntriesService.getNextEntryNumber(2);
      setEntryNumber(nextNumber);
    } catch (err) {
      console.error('Failed to fetch next entry number:', err);
    }
  };

  const loadInventoryBalances = async (periodId?: number | null) => {
    setLoadingInventory(true);
    try {
      // استخدام undefined للحصول على جميع السندات المرحّلة
      const summaries = await inventoryOpeningBalancesService.getSummaryForOpeningEntry();
      
      if (summaries && summaries.length > 0) {
        const lines: DetailLine[] = summaries.map((summary, index) => ({
          id: `inv-${summary.openingBalanceId}-${Date.now()}`,
          lineNumber: index + 1,
          accountId: summary.inventoryAccountId || null,
          accountCode: summary.accountCode,
          accountName: summary.accountName,
          accountNameAr: summary.accountNameAr,
          debit: summary.totalValue,
          credit: 0,
          costCenterId: null,
          description: isRTL 
            ? `مخزون ${summary.warehouseNameAr || summary.branchNameAr || ''}`
            : `Inventory ${summary.warehouseName || summary.branchName || ''}`,
          isInventory: true,
          sourceDocumentNumber: summary.documentNumber,
        }));
        setInventoryLines(lines);
      } else {
        setInventoryLines([]);
      }
    } catch (err) {
      console.error('Failed to load inventory balances:', err);
      setInventoryLines([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  const loadEntry = () => {
    if (!entry) return;
    setEntryNumber(entry.entryNumber);
    setEntryDate(entry.entryDate.split('T')[0]);
    setFiscalPeriodId(entry.fiscalPeriodId || null);
    setDescription(entry.description || '');
    setDescriptionAr(entry.descriptionAr || '');
    setNotes(entry.notes || '');

    const loadedLines: DetailLine[] = entry.details.map((detail, index) => ({
      id: `${detail.entryDetailId}`,
      lineNumber: index + 1,
      accountId: detail.accountId,
      accountCode: detail.accountCode,
      accountName: detail.accountName,
      accountNameAr: detail.accountNameAr,
      debit: detail.debit,
      credit: detail.credit,
      costCenterId: detail.costCenterId || null,
      description: detail.description || '',
      isInventory: false,
    }));

    setManualLines(loadedLines);
    setInventoryLines([]);
  };

  const createNewLine = (lineNumber: number): DetailLine => ({
    id: Date.now().toString() + Math.random(),
    lineNumber,
    accountId: null,
    debit: 0,
    credit: 0,
    costCenterId: null,
    description: '',
    isInventory: false,
  });

  const addManualLine = () => {
    const allLines = [...inventoryLines, ...manualLines];
    const newLine = createNewLine(allLines.length + 1);
    setManualLines([...manualLines, newLine]);
  };

  const removeManualLine = (id: string) => {
    if (manualLines.length === 1) {
      setError(isRTL ? 'يجب أن يحتوي القيد على سطر واحد على الأقل' : 'Entry must have at least one line');
      return;
    }
    const newLines = manualLines.filter(l => l.id !== id);
    newLines.forEach((l, index) => {
      l.lineNumber = inventoryLines.length + index + 1;
    });
    setManualLines(newLines);
  };

  const updateManualLine = (id: string, field: keyof DetailLine, value: any) => {
    setManualLines(manualLines.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const inventoryDebit = inventoryLines.reduce((sum, l) => sum + (l.debit || 0), 0);
  const inventoryCredit = inventoryLines.reduce((sum, l) => sum + (l.credit || 0), 0);
  const manualDebit = manualLines.reduce((sum, l) => sum + (l.debit || 0), 0);
  const manualCredit = manualLines.reduce((sum, l) => sum + (l.credit || 0), 0);
  const totalDebit = inventoryDebit + manualDebit;
  const totalCredit = inventoryCredit + manualCredit;
  const difference = Math.abs(totalDebit - totalCredit);
  const isBalanced = difference < 0.001;

  const validateForm = (): boolean => {
    if (!entryDate) {
      setError(isRTL ? 'التاريخ مطلوب' : 'Date is required');
      return false;
    }
    const allLines = [...inventoryLines, ...manualLines];
    if (allLines.length === 0) {
      setError(isRTL ? 'يجب إضافة سطر واحد على الأقل' : 'At least one line is required');
      return false;
    }
    for (const line of manualLines) {
      if (!line.accountId) {
        setError(`${isRTL ? 'الحساب مطلوب في السطر' : 'Account is required in line'} ${line.lineNumber}`);
        return false;
      }
      if (line.debit === 0 && line.credit === 0) {
        setError(`${isRTL ? 'يجب إدخال مدين أو دائن في السطر' : 'Debit or Credit is required in line'} ${line.lineNumber}`);
        return false;
      }
      if (line.debit > 0 && line.credit > 0) {
        setError(`${isRTL ? 'لا يمكن إدخال مدين ودائن معاً في السطر' : 'Cannot have both Debit and Credit in line'} ${line.lineNumber}`);
        return false;
      }
    }
    if (!isBalanced) {
      setError(isRTL ? 'القيد غير متوازن' : 'Entry is unbalanced');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      setError('');
      const allLines = [...inventoryLines, ...manualLines];
      
      const createDto: CreateJournalEntryDto = {
        entryTypeId: 2,
        entryNumber: entryNumber || undefined,
        entryDate,
        fiscalPeriodId: fiscalPeriodId || undefined,
        sourceType: 'Manual',
        description,
        descriptionAr,
        notes,
        details: allLines.map((l, index) => ({
          lineNumber: index + 1,
          accountId: l.accountId!,
          debit: l.debit,
          credit: l.credit,
          costCenterId: l.costCenterId || undefined,
          description: l.description,
        }))
      };

      await journalEntriesService.create(createDto);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, background: isDarkMode ? 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 5%, #f8f9fa 5%)', height: '95vh' } }}>
      
      <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptLongIcon sx={{ fontSize: 36 }} />
          <Typography variant="h5" fontWeight="bold">{isEdit ? t.editTitle : t.newTitle}</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3, overflowY: 'auto' }}>
        <Stack spacing={3}>
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2 }}>{error}</Alert>}

          {/* Entry Info Card */}
          <Card elevation={4} sx={{ borderRadius: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReceiptLongIcon sx={{ color: '#667eea', fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 'bold' }}>{t.voucherInfo}</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField label={t.entryNumber} value={entryNumber} onChange={(e) => setEntryNumber(e.target.value)} sx={{ flex: '1 1 200px' }} disabled={isEdit}
                  InputProps={{ endAdornment: !isEdit && <IconButton size="small" onClick={fetchNextEntryNumber}><RefreshIcon /></IconButton> }} />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t.entryDate}
                    value={entryDate ? new Date(entryDate) : null}
                    onChange={(date) => setEntryDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                    format="dd/MM/yyyy"
                    views={['year', 'month', 'day']}
                    openTo="day"
                    minDate={minDate}
                    maxDate={maxDate}
                    slotProps={{ textField: { required: true, sx: { flex: '1 1 200px' } } }}
                  />
                </LocalizationProvider>
                <Box sx={{ flex: '1 1 200px' }}><FiscalYearSelector value={fiscalPeriodId} onChange={setFiscalPeriodId} /></Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <TextField label={t.description} value={description} onChange={(e) => setDescription(e.target.value)} sx={{ flex: '1 1 45%' }} />
                <TextField label={t.descriptionAr} value={descriptionAr} onChange={(e) => setDescriptionAr(e.target.value)} sx={{ flex: '1 1 45%' }} />
              </Box>
              <TextField label={t.notes} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} sx={{ mt: 2 }} />
            </CardContent>
          </Card>

          {/* Inventory Section */}
          <Card elevation={5} sx={{ borderRadius: 3, background: isDarkMode ? 'linear-gradient(135deg, rgba(102,126,234,0.15) 0%, rgba(118,75,162,0.15) 100%)' : 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)', border: '2px solid #667eea' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon sx={{ color: '#667eea', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.inventorySection}</Typography>
                  <LockIcon sx={{ color: '#667eea', fontSize: 18 }} />
                </Box>
                <Tooltip title={isRTL ? 'تحديث بيانات المخزون' : 'Refresh inventory data'}>
                  <IconButton onClick={handleRefreshInventory} disabled={loadingInventory}><RefreshIcon /></IconButton>
                </Tooltip>
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>{t.inventoryNote}</Alert>

              {loadingInventory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /><Typography sx={{ ml: 2 }}>{t.loadingInventory}</Typography></Box>
              ) : inventoryLines.length === 0 ? (
                <Alert severity="warning">{t.noInventory}</Alert>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: isDarkMode ? 'rgba(30,30,30,0.95)' : 'white' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#667eea' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.account}</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.sourceDoc}</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.debit}</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.credit}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryLines.map((line, index) => (
                        <TableRow key={line.id} sx={{ bgcolor: isDarkMode ? 'rgba(50,50,50,0.9)' : 'rgba(255,255,255,0.95)' }}>
                          <TableCell><Chip label={index + 1} size="small" color="primary" /></TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" color={isDarkMode ? 'white' : '#333'}>{line.accountCode}</Typography>
                            <Typography variant="caption" color={isDarkMode ? 'grey.400' : 'text.secondary'}>{isRTL ? line.accountNameAr : line.accountName}</Typography>
                          </TableCell>
                          <TableCell><Chip label={line.sourceDocumentNumber} size="small" variant="outlined" sx={{ color: isDarkMode ? 'white' : '#333', borderColor: isDarkMode ? 'grey.500' : 'grey.400' }} /></TableCell>
                          <TableCell align="right"><Typography fontWeight="bold" color="success.main">{line.debit > 0 ? line.debit.toFixed(3) : '-'}</Typography></TableCell>
                          <TableCell align="right"><Typography fontWeight="bold" color="error.main">{line.credit > 0 ? line.credit.toFixed(3) : '-'}</Typography></TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: isDarkMode ? 'rgba(102,126,234,0.3)' : 'rgba(102,126,234,0.15)' }}>
                        <TableCell colSpan={3} align="right"><Typography fontWeight="bold" color={isDarkMode ? 'white' : '#333'}>{isRTL ? 'إجمالي المخزون:' : 'Inventory Total:'}</Typography></TableCell>
                        <TableCell align="right"><Typography fontWeight="bold" color="success.main">{inventoryDebit.toFixed(3)}</Typography></TableCell>
                        <TableCell align="right"><Typography fontWeight="bold" color="error.main">{inventoryCredit.toFixed(3)}</Typography></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Manual Section */}
          <Card elevation={5} sx={{ borderRadius: 3, background: isDarkMode ? 'linear-gradient(135deg, rgba(76,175,80,0.15) 0%, rgba(139,195,74,0.15) 100%)' : 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)', border: '2px solid #4caf50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#4caf50' }}>{t.manualSection}</Typography>
                </Box>
                <Button startIcon={<AddIcon />} onClick={addManualLine} variant="contained" size="small" sx={{ background: 'linear-gradient(45deg, #4caf50 30%, #8bc34a 90%)' }}>{t.addLine}</Button>
              </Box>

              <Box ref={gridRef}>
              <TableContainer component={Paper} sx={{ bgcolor: isDarkMode ? 'rgba(30,30,30,0.95)' : 'white' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#4caf50' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '5%' }}>#</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '30%' }}>{t.account}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12%' }}>{t.debit}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12%' }}>{t.credit}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '20%' }}>{t.costCenter}</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%' }}>{t.description}</TableCell>
                      <TableCell sx={{ width: '6%' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manualLines.map((line, index) => (
                      <TableRow key={line.id} sx={{ bgcolor: isDarkMode ? 'rgba(50,50,50,0.9)' : 'rgba(255,255,255,0.95)' }}>
                        <TableCell><Chip label={inventoryLines.length + index + 1} size="small" color="success" /></TableCell>
                        <TableCell><AccountSelector value={line.accountId} onChange={(id) => updateManualLine(line.id, 'accountId', id)} onlyFinalAccounts={true} /></TableCell>
                        <TableCell><TextField size="small" fullWidth type="number" value={line.debit || ''} onChange={(e) => updateManualLine(line.id, 'debit', parseFloat(e.target.value) || 0)} inputProps={{ min: 0, step: 0.001 }} sx={{ '& .MuiInputBase-input': { color: isDarkMode ? 'white' : '#333' } }} /></TableCell>
                        <TableCell><TextField size="small" fullWidth type="number" value={line.credit || ''} onChange={(e) => updateManualLine(line.id, 'credit', parseFloat(e.target.value) || 0)} inputProps={{ min: 0, step: 0.001 }} sx={{ '& .MuiInputBase-input': { color: isDarkMode ? 'white' : '#333' } }} /></TableCell>
                        <TableCell><CostCenterSelector value={line.costCenterId} onChange={(id) => updateManualLine(line.id, 'costCenterId', id)} /></TableCell>
                        <TableCell><TextField size="small" fullWidth value={line.description} onChange={(e) => updateManualLine(line.id, 'description', e.target.value)} sx={{ '& .MuiInputBase-input': { color: isDarkMode ? 'white' : '#333' } }} /></TableCell>
                        <TableCell><IconButton size="small" color="error" onClick={() => removeManualLine(line.id)} disabled={manualLines.length === 1}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: isDarkMode ? 'rgba(76,175,80,0.3)' : 'rgba(76,175,80,0.15)' }}>
                      <TableCell colSpan={2} align="right"><Typography fontWeight="bold" color={isDarkMode ? 'white' : '#333'}>{isRTL ? 'إجمالي يدوي:' : 'Manual Total:'}</Typography></TableCell>
                      <TableCell><Typography fontWeight="bold" color="success.main">{manualDebit.toFixed(3)}</Typography></TableCell>
                      <TableCell><Typography fontWeight="bold" color="error.main">{manualCredit.toFixed(3)}</Typography></TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>
            </CardContent>
          </Card>

          {/* Totals Card */}
          <Card elevation={6} sx={{ borderRadius: 3, background: isBalanced ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'linear-gradient(135deg, #f85032 0%, #e73827 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight="bold">⚖️ {t.total}</Typography>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}><Typography variant="caption">{t.totalDebit}</Typography><Typography variant="h4" fontWeight="bold">{totalDebit.toFixed(3)}</Typography></Box>
                  <Typography variant="h3" fontWeight="bold">=</Typography>
                  <Box sx={{ textAlign: 'center' }}><Typography variant="caption">{t.totalCredit}</Typography><Typography variant="h4" fontWeight="bold">{totalCredit.toFixed(3)}</Typography></Box>
                  <Typography variant="h2" fontWeight="bold">{isBalanced ? '✅' : '❌'}</Typography>
                </Box>
              </Box>
              {!isBalanced && <Alert severity="error" sx={{ mt: 2, bgcolor: '#ffebee', border: '1px solid #f44336', '& .MuiAlert-message': { color: '#c62828', fontWeight: 'bold', fontSize: '1rem' } }}>{t.difference}: <strong style={{ color: '#b71c1c' }}>{difference.toFixed(3)}</strong> - {t.unbalanced}</Alert>}
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.100' }}>
        <Button onClick={handleClose} disabled={loading} size="large">{t.cancel}</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || !isBalanced} size="large" startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ minWidth: 150, background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)' }}>{loading ? t.processing : t.save}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpeningEntryDialog;